import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
// Hooks
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { deleteApi } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useWindowSize from '@/utils/hooks/utils/useWindowSize';

// Types
import PropTypes from 'prop-types';

// Lib
import { DELETE_SUCCESS } from '@/lib/posts/postLabelsAlbumsEvents';
import { ALBUM_MEDIA } from '@/lib/routes/apiEndpoints';

// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import ActionTag from '@/components/base/Tag/ActionTag';
import { ActionButton } from '@/components/composite/MediaViewer/components/LabelsAlbums/ActionButton';

const CustomPopover = dynamic(() => import('@/components/composite/Popover/CustomPopover'));
const AlbumsPopover = dynamic(() => import('@/components/composite/Post/AlbumsPopover'));

export const AlbumsSection = ({
    socialProfile,
    post,
    postType,
    handleAlbumsEvent = () => {
        /** */
    },
    isOpenAlbums,
    toggle,
    disabled
}) => {
    const { t } = useTranslation();
    const { width: windowWidth } = useWindowSize();
    const [options, setOptions] = useState({
        placement: 'right-end',
        offset: [40, 20]
    });
    const [idAlbumDeleting, setIdAlbumDeleting] = useState(null);
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: options?.placement,
        isReferenceHidden: true,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: options?.offset
                }
            }
        ]
    });

    const updatePopperPosition = useCallback(async () => {
        if (typeof update === 'function') await update();
    }, [update]);

    useEffect(() => {
        if (popperElement) {
            updatePopperPosition();
        }
    }, [updatePopperPosition, post?.albums, popperElement]);

    useEffect(() => {
        if (windowWidth > 675) {
            if (options?.placement !== 'right-end') {
                setOptions({
                    placement: 'right-end',
                    offset: [40, 20]
                });
            }
        } else {
            if (options?.placement !== 'bottom-start') {
                setOptions({
                    placement: 'bottom-start',
                    offset: [0, 25]
                });
            }
        }
    }, [windowWidth]);

    const handleDeleteAlbum = async (album) => {
        try {
            setIdAlbumDeleting(album?.id);
            await deleteApi(
                `${injectEndpointUrlParams(ALBUM_MEDIA, {
                    SPID: socialProfile?.id,
                    ALBUM_ID: album?.id
                })}/${post?.id}`
            );
            setIdAlbumDeleting(null);
            handleAlbumsEvent(album, DELETE_SUCCESS);
        } finally {
            setIdAlbumDeleting(null);
        }
    };

    return (
        <>
            <ul className="ml-2 flex flex-wrap">
                {post?.albums?.map((album, index) => {
                    if (album?.type === 'manual') {
                        return (
                            <li className="mb-2 mr-2 max-w-sm md:max-w-md" key={index}>
                                <ActionTag variant="album" icon="albums">
                                    <span className="mr-1 truncate" title={album?.name}>
                                        {album?.name}
                                    </span>
                                    {idAlbumDeleting === album?.id ? (
                                        <Loader className="h-3 w-3 text-white" />
                                    ) : (
                                        <button
                                            aria-label="delete-label"
                                            className="leading-none"
                                            onClick={() => handleDeleteAlbum(album)}>
                                            <Icon icon="close" className="align-bottom" />
                                        </button>
                                    )}
                                </ActionTag>
                            </li>
                        );
                    }
                    if (album?.type === 'auto') {
                        return (
                            <li className="mb-2 mr-2 max-w-sm md:max-w-md" key={index}>
                                <ActionTag variant="album" icon="albums">
                                    <span className="mr-1 truncate" title={album?.name}>
                                        {album?.name}
                                    </span>
                                </ActionTag>
                            </li>
                        );
                    }
                    return null;
                })}
                <ActionButton
                    className={clsx('mb-2', { 'bg-icoBlue text-white': !disabled })}
                    icon="albums"
                    onClick={() => toggle(!isOpenAlbums)}
                    aria-label="add-album"
                    setReferenceElement={setReferenceElement}
                    title={t('commons:media.albums.add_to_album')}
                    disabled={disabled}
                />
            </ul>
            {isOpenAlbums
                ? ReactDOM.createPortal(
                      <CustomPopover
                          open={isOpenAlbums}
                          popperElement={popperElement}
                          setPopperElement={setPopperElement}
                          styles={styles}
                          toggle={() => toggle(false)}
                          attributes={attributes}
                          width="w-80 max-w-[calc(100vw-38px)]"
                          wrapperClassName="z-1"
                          isBorder>
                          <AlbumsPopover
                              mediaAlbums={post?.albums}
                              mediaId={post?.id}
                              handleAlbumsEvent={handleAlbumsEvent}
                              postType={postType}
                              socialProfile={socialProfile}
                              type="mediaViewer"
                          />
                      </CustomPopover>,
                      document.getElementById('media-viewer-content')
                  )
                : null}
        </>
    );
};

AlbumsSection.propTypes = {
    socialProfile: PropTypes.object,
    post: PropTypes.object,
    postType: PropTypes.string,
    handleLabelsEvent: PropTypes.func,
    isOpenAlbums: PropTypes.bool,
    toggle: PropTypes.func
};
